import { useState, useEffect } from 'react'
import {
  FileText,
  FileSpreadsheet,
  Image,
  File,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Receipt,
  Building2,
  FileCheck,
  FileSignature,
  Users,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Tag,
  Calendar,
  Euro,
  User,
  ArrowRight,
  Eye,
} from 'lucide-react'
import type { 
  DocumentRecognitionResult, 
  RecognitionStep,
  DocumentType,
  PrimaNotaProposal 
} from '../services/documentClassifier'
import { useLanguage } from '../hooks/useLanguage'

// ─── Document Type Icons ─────────────────────────────────────────────────────

const DOC_TYPE_ICONS: Record<DocumentType, React.ReactNode> = {
  invoice: <Receipt className="w-5 h-5" />,
  receipt: <Receipt className="w-5 h-5" />,
  bank_statement: <Building2 className="w-5 h-5" />,
  tax_form: <FileCheck className="w-5 h-5" />,
  contract: <FileSignature className="w-5 h-5" />,
  payslip: <Users className="w-5 h-5" />,
  chart_accounts: <FileSpreadsheet className="w-5 h-5" />,
  trial_balance: <FileCheck className="w-5 h-5" />,
  balance_sheet: <FileText className="w-5 h-5" />,
  other: <File className="w-5 h-5" />,
}

const DOC_TYPE_COLORS: Record<DocumentType, string> = {
  invoice: 'text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400',
  receipt: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400',
  bank_statement: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400',
  tax_form: 'text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400',
  contract: 'text-teal-600 bg-teal-100 dark:bg-teal-900/30 dark:text-teal-400',
  payslip: 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400',
  chart_accounts: 'text-indigo-600 bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400',
  trial_balance: 'text-cyan-600 bg-cyan-100 dark:bg-cyan-900/30 dark:text-cyan-400',
  balance_sheet: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400',
  other: 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-400',
}

const FILE_TYPE_ICONS: Record<string, React.ReactNode> = {
  csv: <FileSpreadsheet className="w-4 h-4 text-green-600" />,
  xlsx: <FileSpreadsheet className="w-4 h-4 text-green-600" />,
  xls: <FileSpreadsheet className="w-4 h-4 text-green-600" />,
  pdf: <FileText className="w-4 h-4 text-red-500" />,
  image: <Image className="w-4 h-4 text-indigo-500" />,
  unknown: <File className="w-4 h-4 text-gray-400" />,
}

// ─── Step Indicator ──────────────────────────────────────────────────────────

function StepIndicator({ step, index }: { step: RecognitionStep; index: number }) {
  return (
    <div className="flex items-center gap-3">
      {/* Step number/icon */}
      <div className={`
        flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold 
        transition-all duration-300
        ${step.status === 'completed' 
          ? 'bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-400' 
          : step.status === 'running' 
          ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/50 dark:text-orange-400 animate-pulse' 
          : step.status === 'error'
          ? 'bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400'
          : 'bg-gray-100 text-gray-400 dark:bg-slate-700 dark:text-slate-500'
        }
      `}>
        {step.status === 'completed' ? (
          <CheckCircle2 className="w-4 h-4" />
        ) : step.status === 'running' ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : step.status === 'error' ? (
          <AlertCircle className="w-4 h-4" />
        ) : (
          index + 1
        )}
      </div>
      
      {/* Step text */}
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-medium transition-colors duration-300 ${
          step.status === 'completed' ? 'text-gray-900 dark:text-white' 
          : step.status === 'running' ? 'text-orange-700 dark:text-orange-300'
          : 'text-gray-400 dark:text-slate-500'
        }`}>
          {step.label}
        </p>
        {step.detail && step.status === 'completed' && (
          <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
            {step.detail}
          </p>
        )}
      </div>
    </div>
  )
}

// ─── Confidence Bar ──────────────────────────────────────────────────────────

function ConfidenceBar({ value, size = 'sm' }: { value: number; size?: 'sm' | 'md' }) {
  const percentage = Math.round(value * 100)
  const color = percentage >= 80 ? 'bg-green-500' : percentage >= 50 ? 'bg-amber-500' : 'bg-red-500'
  
  return (
    <div className="flex items-center gap-2">
      <div className={`flex-1 ${size === 'md' ? 'h-2' : 'h-1.5'} bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden`}>
        <div 
          className={`h-full ${color} rounded-full transition-all duration-700 ease-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className={`${size === 'md' ? 'text-xs' : 'text-[11px]'} font-medium text-gray-500 dark:text-gray-400 tabular-nums`}>
        {percentage}%
      </span>
    </div>
  )
}

// ─── Prima Nota Card ─────────────────────────────────────────────────────────

function PrimaNotaCard({ proposal, language }: { proposal: PrimaNotaProposal; language: string }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-primary-300 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/20 p-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="p-1.5 rounded-lg bg-primary-100 dark:bg-primary-900/50">
          <Sparkles className="w-4 h-4 text-primary-600 dark:text-primary-400" />
        </div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
          {language === 'it' ? 'Proposta Prima Nota' : 'Journal Entry Proposal'}
        </h4>
        <span className={`ml-auto px-2 py-0.5 text-[11px] font-medium rounded-full ${
          proposal.type === 'expense' 
            ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400' 
            : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
        }`}>
          {proposal.type === 'expense' 
            ? (language === 'it' ? 'Uscita' : 'Expense') 
            : (language === 'it' ? 'Entrata' : 'Income')
          }
        </span>
      </div>
      
      {/* Fields */}
      <div className="grid grid-cols-2 gap-3">
        {proposal.date && (
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                {language === 'it' ? 'Data' : 'Date'}
              </p>
              <p className="text-xs font-medium text-gray-900 dark:text-white">
                {new Date(proposal.date).toLocaleDateString('it-IT')}
              </p>
            </div>
          </div>
        )}
        
        {proposal.amount > 0 && (
          <div className="flex items-center gap-2">
            <Euro className="w-3.5 h-3.5 text-gray-400" />
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                {language === 'it' ? 'Importo' : 'Amount'}
              </p>
              <p className="text-xs font-medium text-gray-900 dark:text-white">
                € {proposal.amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        )}
        
        {proposal.supplier && (
          <div className="flex items-center gap-2">
            <User className="w-3.5 h-3.5 text-gray-400" />
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                {language === 'it' ? 'Fornitore' : 'Supplier'}
              </p>
              <p className="text-xs font-medium text-gray-900 dark:text-white truncate max-w-[140px]">
                {proposal.supplier}
              </p>
            </div>
          </div>
        )}
        
        <div className="flex items-center gap-2">
          <Tag className="w-3.5 h-3.5 text-gray-400" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">
              {language === 'it' ? 'Categoria' : 'Category'}
            </p>
            <p className="text-xs font-medium text-primary-700 dark:text-primary-400 truncate max-w-[140px]">
              {proposal.suggestedCategory}
            </p>
          </div>
        </div>
      </div>

      {proposal.tax && proposal.tax > 0 && (
        <div className="mt-2 pt-2 border-t border-primary-200 dark:border-primary-800 flex items-center gap-2">
          <span className="text-[11px] text-gray-500 dark:text-gray-400">
            IVA: € {proposal.tax.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
          </span>
          <span className="text-[11px] text-gray-400">•</span>
          <span className="text-[11px] text-gray-500 dark:text-gray-400">
            {language === 'it' ? 'Costo' : 'Cost'}: {proposal.suggestedCostType === 'fixed' 
              ? (language === 'it' ? 'Fisso' : 'Fixed') 
              : (language === 'it' ? 'Variabile' : 'Variable')
            }
          </span>
        </div>
      )}
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

interface DocumentRecognitionProps {
  fileName: string
  result: DocumentRecognitionResult | null
  steps: RecognitionStep[]
  isProcessing: boolean
}

export default function DocumentRecognition({ fileName, result, steps, isProcessing }: DocumentRecognitionProps) {
  const { language } = useLanguage()
  const [expanded, setExpanded] = useState(true)
  const [showTextPreview, setShowTextPreview] = useState(false)

  // Auto-collapse after a few seconds when done
  useEffect(() => {
    if (result && !isProcessing) {
      const timer = setTimeout(() => {
        // Keep expanded if there's a prima nota proposal
        if (!result.primaNota) {
          setExpanded(false)
        }
      }, 8000)
      return () => clearTimeout(timer)
    }
  }, [result, isProcessing])
  
  const allCompleted = steps.every(s => s.status === 'completed')
  
  return (
    <div className={`
      rounded-xl border overflow-hidden transition-all duration-300
      ${isProcessing 
        ? 'border-orange-400 dark:border-orange-600 bg-white dark:bg-slate-800 shadow-lg shadow-orange-100 dark:shadow-orange-900/20' 
        : allCompleted 
        ? 'border-green-200 dark:border-green-800 bg-white dark:bg-slate-800'
        : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800'
      }
    `}>
      {/* Header */}
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
      >
        {/* File type icon */}
        <div className={`p-2 rounded-lg ${
          result ? DOC_TYPE_COLORS[result.documentType] : 'bg-gray-100 dark:bg-slate-700 text-gray-500'
        }`}>
          {result ? DOC_TYPE_ICONS[result.documentType] : FILE_TYPE_ICONS[fileName.split('.').pop()?.toLowerCase() || 'unknown']}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {fileName}
            </p>
            {isProcessing && (
              <span className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 rounded-full animate-pulse">
                <Loader2 className="w-3 h-3 animate-spin" />
                {language === 'it' ? 'Elaborazione in corso...' : 'Processing...'}
              </span>
            )}
          </div>
          {result && !isProcessing && (
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {result.documentTypeLabel}
              </span>
              <span className="text-gray-300 dark:text-gray-600">•</span>
              <ConfidenceBar value={result.confidence} />
            </div>
          )}
        </div>
        
        {/* Summary badge (when collapsed) */}
        {!expanded && result?.primaNota && (
          <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            {result.primaNota.supplier && (
              <span className="px-2 py-0.5 bg-gray-100 dark:bg-slate-700 rounded-full">
                {result.primaNota.supplier}
              </span>
            )}
            <ArrowRight className="w-3 h-3" />
            <span className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 rounded-full">
              {result.primaNota.suggestedCategory}
            </span>
          </div>
        )}
        
        {/* Expand/collapse */}
        <div className="text-gray-400">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>
      
      {/* Expandable content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 dark:border-slate-700 pt-3 animate-fadeIn">
          {/* Pipeline steps */}
          <div className="space-y-2">
            {steps.map((step, i) => (
              <StepIndicator key={step.id} step={step} index={i} />
            ))}
          </div>
          
          {/* Skeleton fields while processing */}
          {isProcessing && (!result || result.extractedFields.length === 0) && (
            <div className="rounded-lg bg-gradient-to-br from-orange-50/80 to-amber-50/60 dark:from-orange-900/20 dark:to-amber-900/10 border border-orange-200/50 dark:border-orange-800/30 p-3">
              <div className="flex items-center gap-2 mb-3">
                <Loader2 className="w-3.5 h-3.5 text-orange-500 animate-spin" />
                <h4 className="text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wider">
                  {language === 'it' ? 'Campi in estrazione...' : 'Extracting fields...'}
                </h4>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[language === 'it' ? 'Tipo' : 'Type', language === 'it' ? 'Data' : 'Date', language === 'it' ? 'Importo' : 'Amount', language === 'it' ? 'Fornitore' : 'Supplier'].map((label, i) => (
                  <div key={i}>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500">{label}</p>
                    <div className="h-4 mt-0.5 rounded bg-gradient-to-r from-orange-200/60 via-orange-100/30 to-orange-200/60 dark:from-orange-800/30 dark:via-orange-700/15 dark:to-orange-800/30 animate-shimmer"
                      style={{ width: `${60 + (i * 15) % 35}%`, animationDelay: `${i * 250}ms` }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Extracted fields */}
          {result && result.extractedFields.length > 0 && (
            <div className="rounded-lg bg-gray-50 dark:bg-slate-700/50 p-3">
              <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2 uppercase tracking-wider">
                {language === 'it' ? 'Campi Estratti' : 'Extracted Fields'}
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {result.extractedFields.map((field, i) => (
                  <div key={i} className="flex items-start justify-between gap-1">
                    <div className="min-w-0">
                      <p className="text-[10px] text-gray-400 dark:text-gray-500">{field.label}</p>
                      <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{field.value}</p>
                    </div>
                    <span className={`text-[10px] tabular-nums mt-1 ${
                      field.confidence >= 0.8 ? 'text-green-500' : field.confidence >= 0.5 ? 'text-amber-500' : 'text-red-500'
                    }`}>
                      {Math.round(field.confidence * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Prima nota proposal */}
          {result?.primaNota && (
            <PrimaNotaCard proposal={result.primaNota} language={language} />
          )}
          
          {/* Text preview toggle */}
          {result?.rawTextPreview && (
            <div>
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  setShowTextPreview(!showTextPreview)
                }}
                className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <Eye className="w-3 h-3" />
                {showTextPreview 
                  ? (language === 'it' ? 'Nascondi anteprima testo' : 'Hide text preview') 
                  : (language === 'it' ? 'Mostra anteprima testo' : 'Show text preview')
                }
              </button>
              {showTextPreview && (
                <pre className="mt-2 p-2 text-[10px] text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-slate-900 rounded-lg overflow-x-auto max-h-32 overflow-y-auto font-mono">
                  {result.rawTextPreview}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
